import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useCart } from '../contexts/CartContext'; // A kosár ürítéséhez
import paymentService from '../services/paymentService'; // ÚJ: paymentService a backend hívásához

const PaymentStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart, removeFromCart } = useCart(); // Kosár műveletek
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      const queryParams = new URLSearchParams(location.search);
      const internalPaymentId = queryParams.get('paymentId'); // A mi belső paymentId-nk

      if (!internalPaymentId) {
        setError('Hiányzó fizetés azonosító.');
        setLoading(false);
        return;
      }

      try {
        let response = await paymentService.getPaymentStatus(internalPaymentId); // Backend hívás
        // A Barion-visszaigazolás pár másodpercet késhet: rövid ideig újrakérdezzük
        for (let attempt = 0; attempt < 5 && response.success && response.data?.status === 'PENDING'; attempt += 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          response = await paymentService.getPaymentStatus(internalPaymentId);
        }
        if (response.success) {
          setPaymentStatus(response.data);
          // Ha a fizetés sikeres, ürítsük a kosárból a kifizetett elemeket
          if (response.data.bookings) {
            response.data.bookings.forEach(booking => removeFromCart(booking.id));
          }
        } else {
          setError(response.message || 'Hiba a fizetés státuszának lekérésekor.');
        }
      } catch (err) {
        console.error('Error fetching payment status:', err);
        setError('Hiba a fizetés státuszának lekérésekor.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentStatus();
  }, [location.search, removeFromCart]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Fizetés státuszának lekérése...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/kosar')}>
          Vissza a kosárhoz
        </Button>
      </Container>
    );
  }

  if (!paymentStatus) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Nincs elérhető fizetési státusz.</Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/dashboard')}>
          Vissza a Dashboardra
        </Button>
      </Container>
    );
  }

  const isSuccess = paymentStatus.status === 'SUCCEEDED';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {isSuccess ? (
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
        ) : (
          <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
        )}
        <Typography variant="h4" gutterBottom>
          Fizetés {isSuccess ? 'sikeres!' : 'sikertelen!'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Tranzakció azonosító: {paymentStatus.barionPaymentId || 'N/A'}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" align="left" gutterBottom>
          Fizetési részletek:
        </Typography>
        <List sx={{ textAlign: 'left' }}>
          <ListItem>
            <ListItemText primary="Státusz:" secondary={paymentStatus.status} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Összeg:" secondary={`${paymentStatus.totalAmount} ${paymentStatus.currency}`} />
          </ListItem>
          {paymentStatus.bookings && paymentStatus.bookings.length > 0 && (
            <>
              <ListItem>
                <ListItemText primary="Foglalások:" />
              </ListItem>
              {paymentStatus.bookings.map((booking) => (
                <ListItem key={booking.id} sx={{ pl: 4 }}>
                  <ListItemText
                    primary={`ID: ${booking.id} - ${booking.eventType}`}
                    secondary={`Szolgáltató: ${booking.provider?.businessName || 'N/A'} | Ár: ${booking.finalPrice} ${paymentStatus.currency}`}
                  />
                </ListItem>
              ))}
            </>
          )}
        </List>

        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 4 }}
          onClick={() => navigate('/dashboard')}
        >
          Vissza a Dashboardra
        </Button>
      </Paper>
    </Container>
  );
};

export default PaymentStatusPage;